import { Button, useToast } from '@citizen-walk/shared-ui';
import { useRef, useState } from 'react';
import {
  buildExportFilename,
  exportToJsonFile,
  parseImportedMasters,
  readJsonFile,
  serializeMastersForExport,
} from './jsonImportExport';

/**
 * Sprint BG-MasterIO — M1-M10 master page の一覧ヘッダーに mount する Import/Export 操作群。
 *
 * 構成:
 *   - 📥 JSON 書き出し: 表示中 items[] 全件を envelope ({exportedAt, schemaVersion, items[]}) で download
 *   - 📤 JSON 読込: file input → JSON.parse → validator → 件数確認 confirm → upsert callback 呼出
 *
 * 1 件単位の export は `<RowExportButton>` で別途提供 (各 row 編集ボタン横に追加)。
 *
 * 設計:
 *   - validator は呼出側 page が provide (型ごとに required field チェック)
 *   - onImport callback は upsertMaster の wrap (page 側で uid / collectionName / isNew=判定 を吸収)
 *   - エラーは toast、validator skip 数は confirm dialog に明示
 *   - inline style 禁止 (shared-ui Button + Tailwind utility class)
 */

/** Sprint BG-MasterIO follow-up — file size 上限 (悪意の超大型 JSON 防御)。 */
export const MAX_IMPORT_FILE_BYTES = 5 * 1024 * 1024;

export interface MasterImportExportButtonsProps<T> {
  /** Firestore collection 名 (filename 生成 + 表示用)。 */
  collectionName: string;
  /** Export 対象の表示中 items[] (アーカイブ除外済)。 */
  items: T[];
  /**
   * 1 件 raw を T 型に validate (失敗時 null)。
   * 内部で try/catch するため throw でも OK (parseImportedMasters が errors に集約)。
   */
  validator: (raw: unknown) => T | null;
  /**
   * import 確認後に呼ばれる callback。upsertMaster を items[] でループする想定。
   * upsert 内部の成功/失敗は呼出側 toast 推奨 (本 component では完了 toast のみ)。
   */
  onImport: (items: T[]) => Promise<void>;
  /** ボタンを disable する条件 (編集中など)。 */
  disabled?: boolean;
  /**
   * Sprint BG-MasterIO follow-up — 許可する mode 値。
   * 異なる mode の item は skip し、件数を errors に追記。
   *   - 単一 mode page (例: 'ecw'): 指定 mode のみ受け入れ
   *   - 複数 mode page (例: ['ecw','bousai','walker','kids-bousai']): 列挙したいずれか
   *   - 未指定: mode チェックなし (M2 のように mode 概念が無い master 用)
   *
   * validator 通過後の item の `mode` field を見る (typeof string)。
   */
  expectedMode?: string | readonly string[];
}

export function MasterImportExportButtons<T>(props: MasterImportExportButtonsProps<T>) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importing, setImporting] = useState(false);

  function handleExport() {
    try {
      const envelope = serializeMastersForExport(props.items);
      const filename = buildExportFilename(props.collectionName);
      exportToJsonFile(envelope, filename);
      toast('success', `${props.items.length} 件を ${filename} に書き出しました`);
    } catch (e) {
      toast('error', `書き出し失敗: ${(e as Error).message}`);
    }
  }

  function triggerImport() {
    fileInputRef.current?.click();
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // 同じ file を再選択しても change 発火するよう先に reset
    e.target.value = '';
    if (!file) return;
    // Sprint BG-MasterIO follow-up — file size 上限チェック (parse 前に弾く)
    if (file.size > MAX_IMPORT_FILE_BYTES) {
      toast(
        'error',
        `ファイルサイズが大きすぎます (上限 ${MAX_IMPORT_FILE_BYTES / 1024 / 1024}MB、現在 ${(file.size / 1024 / 1024).toFixed(1)}MB)`,
      );
      return;
    }
    setImporting(true);
    try {
      const raw = await readJsonFile(file);
      const parsed = parseImportedMasters(raw, props.validator);
      let items = parsed.items;
      const errors = [...parsed.errors];

      // Sprint BG-MasterIO follow-up — mode 不一致 item の skip + 件数報告
      if (props.expectedMode !== undefined) {
        const allowed = Array.isArray(props.expectedMode)
          ? (props.expectedMode as readonly string[])
          : [props.expectedMode as string];
        const wrongMode: T[] = [];
        const accepted: T[] = [];
        for (const it of items) {
          const m = (it as { mode?: unknown }).mode;
          if (typeof m === 'string' && allowed.includes(m)) {
            accepted.push(it);
          } else {
            wrongMode.push(it);
          }
        }
        if (wrongMode.length > 0) {
          errors.push(
            `${wrongMode.length} 件が異なる mode のため skip (許可: ${allowed.join(', ')})`,
          );
        }
        items = accepted;
      }

      if (items.length === 0) {
        toast('error', `取込可能な item がありません (失敗 ${errors.length} 件)`);
        return;
      }
      const msg =
        errors.length > 0
          ? `${items.length} 件を取込みます (検証失敗 ${errors.length} 件は skip)。\n\n失敗詳細:\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n…他 ${errors.length - 5} 件` : ''}\n\n続行しますか?`
          : `${items.length} 件を取込みます。続行しますか?`;
      if (!confirm(msg)) return;
      await props.onImport(items);
      toast('success', `${items.length} 件を取込みました`);
    } catch (err) {
      toast('error', `取込失敗: ${(err as Error).message}`);
    } finally {
      setImporting(false);
    }
  }

  return (
    <span className="inline-flex gap-2 flex-wrap">
      <Button
        variant="secondary"
        size="md"
        onClick={handleExport}
        disabled={props.disabled || props.items.length === 0}
        aria-label={`${props.collectionName} を JSON で書き出し`}
      >
        📥 JSON 書き出し
      </Button>
      <Button
        variant="secondary"
        size="md"
        onClick={triggerImport}
        disabled={props.disabled || importing}
        aria-label={`${props.collectionName} に JSON を取込`}
      >
        {importing ? '取込中…' : '📤 JSON 読込'}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={(e) => void handleFile(e)}
        aria-hidden="true"
      />
    </span>
  );
}

export interface RowExportButtonProps<T> {
  /** Firestore collection 名 (filename 用)。 */
  collectionName: string;
  /** Export する 1 件の master object (id を含む)。 */
  item: T & { id: string; displayName?: string };
}

/**
 * 個別 row export ボタン。「編集」ボタン横に追加して 1 件単位で JSON DL する用途。
 * envelope 形式 (items: [item]) で書き出すことで、import 時と schema 統一。
 */
export function RowExportButton<T>(props: RowExportButtonProps<T>) {
  const { toast } = useToast();
  function handleClick() {
    try {
      const envelope = serializeMastersForExport([props.item]);
      const filename = buildExportFilename(props.collectionName, props.item.id);
      exportToJsonFile(envelope, filename);
      toast('success', `${filename} に書き出しました`);
    } catch (e) {
      toast('error', `書き出し失敗: ${(e as Error).message}`);
    }
  }
  const label = props.item.displayName ?? props.item.id;
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={handleClick}
      aria-label={`${label} を JSON で書き出し`}
    >
      📥 JSON
    </Button>
  );
}
